function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

const diff = require('../utils/diff');

function _call(body, then, direct) {
  if (direct) {
    return then ? then(body()) : body();
  }

  try {
    var result = Promise.resolve(body());
    return then ? result.then(then) : result;
  } catch (e) {
    return Promise.reject(e);
  }
}

const AccessContext = require('./contexts/access-context');

const User = require('../entities/user');

const {
  strinct,
  strList
} = require('../utils/serialize');

function parse(json) {
  return new User(json.id, json.email, json.name);
}

function serialize(data) {
  return {
    email: data.email,
    name: strinct(data.name),
    enabled: true
  };
}

const fields = ['id', 'email', 'name'];

class UserRepo {
  constructor(client
  /*: ApiClient*/
  , id
  /*: userId*/
  , context
  /*: AccessContext*/
  = new AccessContext(null)) {
    this.client = client;
    this.id = id;
    this.context = context;
  }

  details()
  /*: (Promise<User>)*/
  {
    const url = `/users/${this.id}`;
    const req = new this.client.Request(url, {}, fields);
    req.parse = parse;
    return this.client.get(req);
  }

  update(inst
  /*: userId | User*/
  , updates
  /*: User | Object*/
  ) {
    const id = inst instanceof User ? inst.id : inst;
    if (!id) throw new Error('Cannot update user without id');
    let updatedProps = updates;

    if (inst instanceof User) {
      updatedProps = diff(serialize(inst), serialize(updates));
    }

    const req = new this.client.Request(`/users/${id}`, updatedProps, fields);
    req.parse = parse;
    req.serialize = serialize;
    return this.client.patch(req);
  }

  properties() {
    const req = new this.client.Request(`/users/${this.id}/properties/search`, {}, ['id', 'name']);
    return this.client.get(req);
  }
  /**
   * Remove from Context
   * TODO: Delete User if no remaining Accesses?
   * @returns {Promise<boolean>}
   */


  remove({
    /*: { recursive: boolean }*/

    recursive = false
  } = {})
  /*: (Promise<boolean>)*/
  {
    return this.client.access.demoteAll(this.context, this.id, recursive);
  }

  demote(roleId
  /*: string*/
  )
  /*: (Promise<boolean>)*/
  {
    return this.client.access.demote(this.context, this.id, roleId);
  }

  accesses()
  /*: Promise<PagedList<Object>>*/
  {
    return this.client.access.list(this.context, this.id);
  }
  /**
   * Send Activation Email
   * @returns {Promise<Boolean>}
   */


  activate()
  /*: Promise<boolean>*/
  {
    const req = new this.client.Request(`/users/${this.id}/auth/activate`, {}, []);
    return this.client.post(req).catch(err => {
      if (err.messageId === '11-67-002') {
        // Unexpected User Status
        return true;
      }

      throw err;
    });
  }

  status()
  /*: (Promise<boolean>)*/
  {
    const url = `/users/${this.id}/status`;
    const req = new this.client.Request(url, {}, ['status']);
    return this.client.get(req);
  }
  /**
   * Completely remove User from the system
   * @returns {Promise<boolean>}
   */


  delete()
  /*: (Promise<boolean>)*/
  {
    const url = `/users/${this.id}`;
    return this.client.delete(new this.client.Request(url));
  }

  promoteToOwner()
  /*: Promise<boolean>*/
  {
    const _this = this;

    return _call(function () {
      // TODO: Make this work for general contexts
      const {
        propertyId,
        businessId,
        locationId
      } = _this.context.serialize();

      if (!propertyId || !businessId || locationId) throw new Error('Can only promote user to Business owner');
      return _await(_this.client.roles.list(), function (roles) {
        const platformBusinessOwnerRole = roles.items.find(role => role.sid === 'platform-business-owner');
        const propertyBusinessOwnerRole = roles.items.find(role => role.sid === 'business-owner');
        const platformBusinessContext = new AccessContext(null, businessId);
        return _await(_this.client.access.promote(platformBusinessContext, _this.id, platformBusinessOwnerRole.id), function () {
          return _await(_this.client.access.promote(_this.context, _this.id, propertyBusinessOwnerRole.id), function () {
            return true;
          });
        });
      });
    });
  }

  promoteToManager()
  /*: Promise<boolean>*/
  {
    const _this2 = this;

    return _call(function () {
      // TODO: Make this work for general contexts
      const {
        propertyId,
        businessId,
        locationId
      } = _this2.context.serialize();

      if (!propertyId || !businessId || locationId) throw new Error('Can only promote user to Business manager');
      return _await(_this2.client.roles.list(), function (roles) {
        const propertyBusinessManagerRole = roles.items.find(role => role.sid === 'business-manager');
        return _await(_this2.client.access.promote(_this2.context, _this2.id, propertyBusinessManagerRole.id), function () {
          return true;
        });
      });
    });
  }

  demoteOwner()
  /*: Promise<boolean>*/
  {
    const _this3 = this;

    return _call(function () {
      // TODO: Make this work for general contexts
      const {
        propertyId,
        businessId,
        locationId
      } = _this3.context.serialize();

      if (!propertyId || !businessId || locationId) throw new Error('Can only be used to demote Business owner');
      return _await(_this3.client.roles.list(), function (roles) {
        const platformBusinessOwnerRole = roles.items.find(role => role.sid === 'platform-business-owner');
        const propertyBusinessOwnerRole = roles.items.find(role => role.sid === 'business-owner');
        const platformBusinessContext = new AccessContext(null, businessId);
        return _await(_this3.client.access.demote(_this3.context, _this3.id, propertyBusinessOwnerRole.id), function () {
          return _await(_this3.client.access.demote(platformBusinessContext, _this3.id, platformBusinessOwnerRole.id), function () {
            return true;
          });
        });
      });
    });
  }

  getReferralContext({
    dynamic
  })
  /*: Promise<any>*/
  {
    const _this4 = this;

    return _call(function () {
      const {
        propertyId
      } = _this4.context.serialize();

      const path = `/properties/${propertyId}/users/${_this4.id}/referralContext`;
      const req = new _this4.client.Request(path, {
        dynamic
      }, ['context']);
      return _await(_this4.client.get(req));
    });
  }

  setReferralContext({
    context,
    dynamic
  })
  /*: Promise<any>*/
  {
    const _this5 = this;

    return _call(function () {
      const {
        propertyId
      } = _this5.context.serialize();

      const path = `/properties/${propertyId}/users/${_this5.id}/referralContext`;
      const req = new _this5.client.Request(path, {
        dynamic,
        context
      }, ['context']);
      return _await(_this5.client.post(req));
    });
  }

  getRoles({
    context = []
  } = {})
  /*: Promise<any>*/
  {
    const _this6 = this;

    return _call(function () {
      const {
        propertyId,
        businessId,
        locationId
      } = _this6.context.serialize();

      const userId = _this6.id;
      const path = [propertyId && `/properties/${propertyId}`, businessId && `/businesses/${businessId}`, locationId && `/locations/${locationId}`, `/roles/search`].filter(Boolean).join('');
      const req = new _this6.client.Request(path, {
        userId,
        context: context.length ? strList(context) : undefined
      }, ['id', 'sid', 'name', 'type', 'desc', 'count']);
      return _await(_this6.client.get(req));
    });
  }

  unlink(provider) {
    const _this7 = this;

    return _call(function () {
      const userId = _this7.id;
      const path = `/users/${userId}/auth/unlink`;
      const req = new _this7.client.Request(path, {
        provider
      });
      return _await(_this7.client.post(req));
    });
  }

}

class UsersRepo {
  constructor(client
  /*: ApiClient*/
  , context
  /*: AccessContext*/
  = new AccessContext(null)) {
    this.client = client;
    this.context = context;

    this.for = (id
    /*: userId*/
    ) => new UserRepo(client, id, context);

    this.icanhas = this.for;
  }

  getUserByEmail({
    /*: { email: string, enabled: boolean }*/

    email,
    enabled
  })
  /*: Promise<User>*/
  {
    const url = `/users`;
    const req = new this.client.Request(url, {
      email,
      enabled
    }, fields);
    req.parse = parse;
    return this.client.get(req).then(list => list.items[0]);
  }

  search({
    /*: {
        email?: ?string,
        query?: ?string,
        global: boolean
      }*/

    email,
    query,
    global = false
  } = {})
  /*: (Promise<PagedList<User>>)*/
  {
    const {
      propertyId
    } = this.context.serialize();
    const path = propertyId && !global ? `/properties/${propertyId}/users/search` : `/users/search`;
    const req = new this.client.Request(path, {
      q: query,
      email
    }, fields);
    req.parse = parse;
    return this.client.get(req);
  }
  /**
   * @async
   * Access-based user search
   *
   * Use this to search for users who match the search query, and have
   * specific property/business/location access, based on repo.context.
   *
   * @param {object} o - Params object
   * @param {RequestOptions} [options]
   */


  searchUsers({
    /*: {
        query?: string,
        roleIds?: string[],
        context?: string[],
        transform?: (PagedList) => PagedList<Object>,
        limit?: int
      }*/

    query,
    roleIds,
    context,
    transform,
    limit = 50
  } = {}, options
  /*: RequestOptions*/
  = {})
  /*: (Promise<PagedList<Object>>)*/
  {
    const _this8 = this;

    return _call(function () {
      const {
        propertyId,
        businessId,
        locationId
      } = _this8.context && _this8.context.serialize() || {};
      const url = [propertyId ? `/properties/${propertyId}` : '', businessId ? `/businesses/${businessId}` : '', locationId ? `/locations/${locationId}` : '', `/accesses/users/search`].join('');
      const req = new _this8.client.Request(url, {
        limit,
        q: strinct(query),
        roleIds: strList(roleIds),
        context: strList(context)
      }, options.fields ? options.fields : ['roleIds', 'name', 'id', 'email', 'locationIds', 'businessIds']);
      req.transform = transform;
      req.useCursor = true;
      return _await(_this8.client.get(req));
    });
  } // TODO: Remove when all uses are changed to search


  listRaw()
  /*: (Promise<PagedList<User>>)*/
  {
    return this.search();
  } // Raw User creation


  create(newUser
  /*: User*/
  )
  /*: Promise<User>*/
  {
    const url = `/users`;
    const req = new this.client.Request(url, newUser, ['id', 'email', 'name', 'enabled']);
    req.parse = parse;
    req.serialize = serialize;
    return this.client.post(req);
  } // Raw User activation


  activate(userId
  /*: string*/
  )
  /*: (Promise<boolean>)*/
  {
    const url = `/users/${userId}/auth/activate`;
    const req = new this.client.Request(url);
    return this.client.post(req);
  }

  createAndActivate(newUser
  /*: User*/
  )
  /*: Promise<User>*/
  {
    const _this9 = this;

    return _call(function () {
      // $FlowFixMe - Why does Flow think user is going to be `Promise | User`????
      return _await(_this9.create(newUser), function (user) {
        return _await(_this9.activate(user.id), function () {
          return user;
        });
      });
    });
  }

  findOrCreate(user
  /*: User*/
  , sendActivation
  /*: boolean*/
  )
  /*: (Promise<User>)*/
  {
    return this.getUserByEmail({
      email: user.email,
      enabled: true
    }).then(maybeUser => {
      if (maybeUser) return maybeUser;
      return sendActivation ? this.createAndActivate(user) : this.create(user);
    });
  }

  invite(newUser
  /*: User*/
  , role
  /*: Role | Array<Role>*/
  , sendActivation
  /*: boolean*/
  )
  /*: (Promise<User>)*/
  {
    const _this10 = this;

    return _call(function () {
      return _await(_this10.findOrCreate(newUser, sendActivation), function (user) {
        if (!user.id) return user;
        const roles = [].concat(role);
        return _await(Promise.all(roles.map(r => _this10.client.access.promote(_this10.context, user.id, r.id))), function () {
          return user;
        });
      });
    });
  }

  signup(newUser
  /*: User*/
  , attributionContext
  /*: [string, Object]*/
  )
  /*: Promise<any>*/
  {
    const _this11 = this;

    return _call(function () {
      const url = `/users/signup`;
      const req = new _this11.client.Request(url, {
        name: newUser.name,
        email: newUser.email,
        context: attributionContext
      });
      req.authType = req.AUTH_TYPES.AUTH_FORBIDDEN;
      return _await(_this11.client.post(req));
    });
  }

  signupActivate(email
  /*: String*/
  )
  /*: Promise<any>*/
  {
    const _this12 = this;

    return _call(function () {
      const url = `/users/signup/activate`;
      const req = new _this12.client.Request(url, {
        email
      });
      req.authType = req.AUTH_TYPES.AUTH_FORBIDDEN;
      return _await(_this12.client.post(req));
    });
  }

}

module.exports = UsersRepo;